import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { GW_DOMAIN } from "@Models/GWDomain";
import { reportError } from "@/utils/traceup/traceup";

/**
 * Instancia de axios configurada con interceptores para:
 * - Obtener token fresco de localStorage en cada request
 * - Manejar errores 401 (unauthorized)
 * - Timeout de 15 segundos
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: "",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// URLs para redirecciones de error
const LOGIN_URL = window.location.origin + "/";

/**
 * Request Interceptor
 * Obtiene el token fresco de localStorage en CADA request
 * Esto soluciona el problema del token estatico que se obtenia una sola vez al cargar el modulo
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Obtener token fresco de localStorage en cada request
    const token = localStorage.getItem("access_token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Maneja errores de autenticacion y servidor
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error: AxiosError): Promise<AxiosError> => {
    const status = error.response?.status;

    if (status === 401) {
      // Token expirado o invalido - limpiar storage y redirigir a login.
      // replace fuerza una carga real aunque ya estemos en la raíz (href a la
      // misma URL no recarga y deja la app montada sin sesión → pantalla en blanco).
      localStorage.clear();
      window.location.replace(LOGIN_URL);
    }

    // Spec 007: suscripción vencida — SubscriptionBlocked (montado en
    // MenuWrapper) escucha este evento y muestra la pantalla de bloqueo.
    const data: any = error.response?.data;
    if (status === 403 && data?.code === "SUBSCRIPTION_EXPIRED") {
      window.dispatchEvent(
        new CustomEvent("subscription-expired", { detail: data })
      );
    }

    // Spec 007: feature no incluida en el plan (compras, marketing/giftcards...).
    // PlanFeatureLocked (montado en MenuWrapper) explica qué plan la incluye en
    // lugar de dejar la vista en un error seco.
    if (status === 403 && data?.code === "PLAN_FEATURE_LOCKED") {
      window.dispatchEvent(
        new CustomEvent("plan-feature-locked", { detail: data })
      );
    }

    // Spec 007: límite de plan alcanzado — PlanLimitPrompt abre el
    // SmartUpgradeModal con el recurso del error (add-on vs upgrade).
    if (data?.code === "PLAN_LIMIT_REACHED") {
      window.dispatchEvent(
        new CustomEvent("plan-limit-reached", { detail: data })
      );
    }

    // Spec 027 — reportar a TraceUp SOLO fallos reales: error de red (sin
    // response) o error de servidor (≥500). Nunca 401/403/4xx (flujo normal).
    // No altera el control de flujo existente (no-op si TraceUp está OFF).
    if (!error.response || (typeof status === "number" && status >= 500)) {
      reportError({
        error,
        source: "frontend-api",
        statusCode: status,
        url: error.config?.url,
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
